import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Box, Paper, Grid, Stack, TextField, MenuItem, Button, Typography, Alert } from '@mui/material';
import { ArrowForwardIos, RestartAlt, PersonAdd } from '@mui/icons-material';
import Notification from '../Notification';
import { useNavigate } from 'react-router-dom';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_TEXT_LENGTH = 100;

const initialValues = {
  first_name: '',
  last_name: '',
  username: '',
  email: '',
  role: 'USER',
  access_role_id: '',
};

// Must match buildDefaultPassword in backend/controllers/userManagementController.js
const previewDefaultPassword = (firstName) => {
  const word = String(firstName).trim().split(/\s+/)[0];
  if (!word) return '';
  return `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}@${new Date().getFullYear()}`;
};

const RequiredLabel = ({ label }) => (
  <span>
    {label}
    <span style={{ color: 'red', marginLeft: '3px' }}>*</span>
  </span>
);

function UserForm({ initialData, onSuccess }) {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' });
  const [roleOptions, setRoleOptions] = useState([]);
  const navigate = useNavigate();

  const isEditMode = !!(initialData && initialData.user_id);

  const valuesFromInitialData = () => (isEditMode
    ? {
        first_name: initialData.first_name || '',
        last_name: initialData.last_name || '',
        username: initialData.username || '',
        email: initialData.email || '',
        role: initialData.role || 'USER',
        access_role_id: initialData.access_role_id || '',
      }
    : initialValues);

  useEffect(() => {
    setValues(valuesFromInitialData());
    setErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  useEffect(() => {
    const loadRoleOptions = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/company-master/access-roles/options`);
        const options = response.data?.data || [];
        setRoleOptions(options);
        // Default a new normal user to the built-in Standard User role.
        if (!isEditMode) {
          const standard = options.find((o) => o.role_name === 'Standard User');
          if (standard) setValues((prev) => (prev.access_role_id ? prev : { ...prev, access_role_id: standard.role_id }));
        }
      } catch (e) {
        setNotify({ isOpen: true, message: 'Failed to load access roles', type: 'error' });
      }
    };
    loadRoleOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validate = () => {
    const temp = {};
    temp.first_name = values.first_name.trim() ? '' : 'First name is required.';
    temp.username = values.username.trim() ? '' : 'Username is required.';
    temp.email = EMAIL_REGEX.test(values.email.trim()) ? '' : 'Enter a valid email.';
    temp.access_role_id = values.role === 'ADMIN' || values.access_role_id ? '' : 'Access role is required.';
    setErrors(temp);
    return Object.values(temp).every((x) => x === '');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'role') {
      // Company admins always get the built-in Company Admin role; normal users cannot keep it.
      const companyAdmin = roleOptions.find((o) => o.role_name === 'Company Admin');
      const standard = roleOptions.find((o) => o.role_name === 'Standard User');
      setValues((prev) => {
        let accessRoleId = prev.access_role_id;
        if (value === 'ADMIN' && companyAdmin) accessRoleId = companyAdmin.role_id;
        if (value === 'USER' && companyAdmin && String(prev.access_role_id) === String(companyAdmin.role_id)) {
          accessRoleId = standard ? standard.role_id : '';
        }
        return { ...prev, role: value, access_role_id: accessRoleId };
      });
      return;
    }
    setValues((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const resetForm = () => {
    setValues(valuesFromInitialData());
    setErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      first_name: values.first_name.trim(),
      last_name: values.last_name.trim(),
      username: values.username.trim(),
      email: values.email.trim(),
      role: values.role,
      access_role_id: values.access_role_id || null,
    };

    const url = isEditMode
      ? `${process.env.REACT_APP_API_BASE_URL}/company-master/users/${initialData.user_id}/update`
      : `${process.env.REACT_APP_API_BASE_URL}/company-master/users`;

    setSubmitting(true);
    try {
      const response = await axios.post(url, payload);
      const type = response.data.emailSent === false ? 'warning' : 'success';
      if (onSuccess) onSuccess(response.data.message, type);
    } catch (e) {
      if (e.response && e.response.status === 401) {
        setNotify({ isOpen: true, message: 'Session Expired!', type: 'error' });
        setTimeout(() => navigate('/'), 1000);
      } else {
        setNotify({ isOpen: true, message: e.response?.data?.message || e.response?.data?.error_message || 'Failed to save user', type: 'error' });
      }
    }
    setSubmitting(false);
  };

  const defaultPassword = previewDefaultPassword(values.first_name);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', margin: '-15px', padding: '30px' }}>
      <Paper elevation={3} sx={{ width: '500px', marginTop: '-10px' }}>
        <Grid className="form-title" container spacing={2}>
          <Grid item>
            <Typography variant="h5" sx={{ textAlign: 'center', margin: 2 }}>
              {isEditMode ? 'Edit User' : 'Add User'}
            </Typography>
          </Grid>
          <Grid item>
            <PersonAdd sx={{ width: 40, height: 40, marginLeft: '-20px' }} />
          </Grid>
        </Grid>
        <form autoComplete="off" onSubmit={handleSubmit}>
          <Stack spacing={3} sx={{ margin: '30px' }}>
            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                name="first_name"
                label={<RequiredLabel label="First Name" />}
                size="small"
                inputProps={{ maxLength: MAX_TEXT_LENGTH }}
                value={values.first_name}
                onChange={handleInputChange}
                {...(errors.first_name && { error: true, helperText: errors.first_name })}
              />
              <TextField
                fullWidth
                name="last_name"
                label="Last Name"
                size="small"
                inputProps={{ maxLength: MAX_TEXT_LENGTH }}
                value={values.last_name}
                onChange={handleInputChange}
              />
            </Stack>

            <TextField
              name="username"
              label={<RequiredLabel label="Username" />}
              size="small"
              inputProps={{ maxLength: MAX_TEXT_LENGTH }}
              value={values.username}
              onChange={handleInputChange}
              {...(errors.username && { error: true, helperText: errors.username })}
            />

            <TextField
              name="email"
              label={<RequiredLabel label="Email (used for login)" />}
              size="small"
              inputProps={{ maxLength: MAX_TEXT_LENGTH }}
              value={values.email}
              onChange={handleInputChange}
              {...(errors.email && { error: true, helperText: errors.email })}
            />

            <TextField
              select
              name="role"
              label={<RequiredLabel label="Role" />}
              size="small"
              value={values.role}
              onChange={handleInputChange}
              helperText={values.role === 'ADMIN' ? 'Company Admin has full access to all modules.' : ''}
            >
              <MenuItem value="USER">User</MenuItem>
              <MenuItem value="ADMIN">Admin</MenuItem>
            </TextField>

            <TextField
              select
              name="access_role_id"
              label={<RequiredLabel label="Access Role" />}
              size="small"
              value={values.access_role_id}
              onChange={handleInputChange}
              disabled={values.role === 'ADMIN'}
              helperText={values.role === 'ADMIN' ? 'Company Admin role is applied automatically.' : 'Controls which menus this user can access.'}
              {...(errors.access_role_id && { error: true, helperText: errors.access_role_id })}
            >
              <MenuItem value="">Select access role</MenuItem>
              {roleOptions.map((option) => (
                <MenuItem key={option.role_id} value={option.role_id}>{option.role_name}</MenuItem>
              ))}
            </TextField>

            {!isEditMode && (
              <Alert severity="info">
                The password will be set automatically as <b>{defaultPassword || 'Firstname@' + new Date().getFullYear()}</b> and
                the login credentials will be emailed to the user. They can change it later using Forgot Password.
              </Alert>
            )}

            <Grid container justifyContent="space-between">
              <Grid item>
                <Button className="form-btn" variant="contained" color="error" onClick={resetForm} endIcon={<RestartAlt />}>
                  Reset
                </Button>
              </Grid>
              <Grid item>
                <Button
                  className="form-btn"
                  variant="contained"
                  type="submit"
                  disabled={submitting}
                  endIcon={<ArrowForwardIos sx={{ width: 18, height: 18 }} />}
                >
                  {submitting ? 'Saving...' : 'Submit'}
                </Button>
              </Grid>
            </Grid>
          </Stack>
        </form>
      </Paper>
      <Notification notify={notify} setNotify={setNotify} />
    </Box>
  );
}

export default UserForm;
